import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  FormControl,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  InputLabel,
} from '@mui/material';
import PostAccess from './PostAccess';

// Import all amenity icons
import liftIcon from '../assets/applift.png';
import cctvIcon from '../assets/appctv.png';
import gymIcon from '../assets/appfit.png';
import gardenIcon from '../assets/appgarden.png';
import kidsAreaIcon from '../assets/appkids.png';
import sportsIcon from '../assets/appsport.png';
import swimmingPoolIcon from '../assets/appswim.png';
import intercomIcon from '../assets/appinter.png';
import gatedCommunityIcon from '../assets/appgate.png';
import clubHouseIcon from '../assets/appclub.png';
import communityHallIcon from '../assets/appcom.png';
import regularWaterSupplyIcon from '../assets/appregul.png';
import powerBackupIcon from '../assets/apppower.png';
import petAllowedIcon from '../assets/apppet.png';
import diningTableIcon from '../assets/appdinner.png';
import washingMachineIcon from '../assets/appwash.png';
import sofaIcon from '../assets/appsofa.png';
import microwaveIcon from '../assets/appmicro.png';
import fridgeIcon from '../assets/appfridge.png';
import waterPurifierIcon from '../assets/appwater.png';
import gasPipelineIcon from '../assets/appgas.png';
import acIcon from '../assets/appac.png';
import bedIcon from '../assets/appbed.png';
import tvIcon from '../assets/apptv.png';
import cupboardIcon from '../assets/appcup.png';
import geyserIcon from '../assets/appgeyser.png';

const societyAmenitiesData = [
  { name: "Lift", icon: liftIcon },
  { name: "CCTV", icon: cctvIcon },
  { name: "Gym", icon: gymIcon },
  { name: "Garden", icon: gardenIcon },
  { name: "Kids Area", icon: kidsAreaIcon },
  { name: "Sports", icon: sportsIcon },
  { name: "Swimming Pool", icon: swimmingPoolIcon },
  { name: "Intercom", icon: intercomIcon },
  { name: "Gated Community", icon: gatedCommunityIcon },
  { name: "Club House", icon: clubHouseIcon },
  { name: "Community Hall", icon: communityHallIcon },
  { name: "Regular Water Supply", icon: regularWaterSupplyIcon },
  { name: "Power backup", icon: powerBackupIcon },
  { name: "Pet Allowed", icon: petAllowedIcon },
];

const furnishingFacilitiesData = [
  { name: "Dining Table", icon: diningTableIcon },
  { name: "Washing Machine", icon: washingMachineIcon },
  { name: "Sofa", icon: sofaIcon },
  { name: "Microwave", icon: microwaveIcon },
  { name: "Fridge", icon: fridgeIcon },
  { name: "Water Purifier", icon: waterPurifierIcon },
  { name: "Gas Pipeline", icon: gasPipelineIcon },
  { name: "AC", icon: acIcon },
  { name: "Bed", icon: bedIcon },
  { name: "TV", icon: tvIcon },
  { name: "Cupboard", icon: cupboardIcon },
  { name: "Geyser", icon: geyserIcon },
];

const otherFacilitiesData = [
  "Hospital", "School", "Park", "Shopping Mall",
  "Metro Station", "Bus Stop", "Restaurant", "Pharmacy"
];

const Amenities = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { propertyType, category, ...otherProps } = location.state || {};

  // ✅ FIXED: Load from location.state first, then localStorage
  const [selectedSociety, setSelectedSociety] = useState(() => {
    // First check location.state (from navigation)
    if (location.state?.society_aminities) {
      return location.state.society_aminities.split(', ').filter(Boolean);
    }
    
    // Then check localStorage
    const saved = localStorage.getItem('amenities_society');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  const [selectedFurnishing, setSelectedFurnishing] = useState(() => {
    // First check location.state
    if (location.state?.furnish_facilities) {
      return location.state.furnish_facilities.split(', ').filter(Boolean);
    }
    
    const saved = localStorage.getItem('amenities_furnishing');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  const [selectedOther, setSelectedOther] = useState(() => {
    // First check location.state
    if (location.state?.other_facilities) {
      return location.state.other_facilities.split(', ').filter(Boolean);
    }
    
    const saved = localStorage.getItem('amenities_other');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  // ✅ Persist to localStorage whenever amenities change
  useEffect(() => {
    localStorage.setItem('amenities_society', JSON.stringify(selectedSociety));
  }, [selectedSociety]);

  useEffect(() => {
    localStorage.setItem('amenities_furnishing', JSON.stringify(selectedFurnishing));
  }, [selectedFurnishing]);

  useEffect(() => {
    localStorage.setItem('amenities_other', JSON.stringify(selectedOther));
  }, [selectedOther]);

  // Calculate progress
  const calculateProgress = () => {
    const totalSelected = selectedSociety.length + selectedFurnishing.length + selectedOther.length;
    const totalAvailable = societyAmenitiesData.length + furnishingFacilitiesData.length + otherFacilitiesData.length;
    return Math.round((totalSelected / totalAvailable) * 100);
  };

  const progress = calculateProgress();

  const handleSocietyChange = (event) => {
    const value = event.target.value;
    setSelectedSociety(typeof value === 'string' ? value.split(',') : value);
  };

  const handleFurnishingChange = (event) => {
    const value = event.target.value;
    setSelectedFurnishing(typeof value === 'string' ? value.split(',') : value);
  };

  const handleOtherChange = (event) => {
    const value = event.target.value;
    setSelectedOther(typeof value === 'string' ? value.split(',') : value);
  };

  const handleDeleteSociety = (itemToDelete) => {
    setSelectedSociety((prev) => prev.filter((item) => item !== itemToDelete));
  };

  const handleDeleteFurnishing = (itemToDelete) => {
    setSelectedFurnishing((prev) => prev.filter((item) => item !== itemToDelete));
  };

  const handleDeleteOther = (itemToDelete) => {
    setSelectedOther((prev) => prev.filter((item) => item !== itemToDelete));
  };

  const handleBack = () => {
    navigate('/property-details', { state: location.state });
  };

  // ✅ FIXED: Don't clear localStorage on navigation
  const handleNext = () => {
    const amenitiesData = {
      ...location.state,
      society_aminities: selectedSociety.length > 0 ? selectedSociety.join(', ') : null,
      furnish_facilities: selectedFurnishing.length > 0 ? selectedFurnishing.join(', ') : null,
      other_facilities: selectedOther.length > 0 ? selectedOther.join(', ') : null,
      facilities_distance: '1km',
    };

    // ✅ DON'T clear localStorage here - keep amenities for back navigation
    // localStorage will be cleared after successful property upload in PriceDetails/RentDetails

    if (category === 'Rent') {
      navigate('/rent-details', { state: amenitiesData });
    } else {
      navigate('/price-details', { state: amenitiesData });
    }
  };

  return (
    <PostAccess
      currentStep={2}
      category={category}
      propertyType={propertyType}
      assetType={location.state?.asset_type || ''}
    >
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          maxWidth: { xs: '100%', md: '900px' },
          p: { xs: 2.5, sm: 3, md: 3.5 },
          backgroundColor: '#ffffff',
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          minHeight: '400px',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: 'Outfit',
            fontWeight: 600,
            mb: 0.5,
            fontSize: { xs: '18px', md: '22px' },
          }}
        >
          Add Amenities
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Outfit',
            fontSize: '13px',
            color: '#666',
            mb: 3,
          }}
        >
          {progress}% <span style={{ color: progress > 75 ? '#4caf50' : progress > 50 ? '#ff9800' : '#2196f3' }}>●●●○○</span>
        </Typography>

        {/* All Dropdowns in One Row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          {/* Society Amenities Dropdown */}
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontFamily: 'Outfit', fontSize: '14px' }}>Society Amenities</InputLabel>
            <Select
              multiple
              value={selectedSociety}
              onChange={handleSocietyChange}
              input={<OutlinedInput label="Society Amenities" />}
              renderValue={(selected) => selected.length > 0 ? `${selected.length} selected` : ''}
              sx={{
                fontFamily: 'Outfit',
                fontSize: '14px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d0d0d0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4f26ff',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4f26ff',
                },
              }}
            >
              {societyAmenitiesData.map((amenity) => (
                <MenuItem
                  key={amenity.name}
                  value={amenity.name}
                  sx={{
                    fontFamily: 'Outfit',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box
                    component="img"
                    src={amenity.icon}
                    alt={amenity.name}
                    sx={{ width: 24, height: 24, objectFit: 'contain' }}
                  />
                  {amenity.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Furnishing Facilities Dropdown */}
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontFamily: 'Outfit', fontSize: '14px' }}>Furnishing Facilities</InputLabel>
            <Select
              multiple
              value={selectedFurnishing}
              onChange={handleFurnishingChange}
              input={<OutlinedInput label="Furnishing Facilities" />}
              renderValue={(selected) => selected.length > 0 ? `${selected.length} selected` : ''}
              sx={{
                fontFamily: 'Outfit',
                fontSize: '14px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d0d0d0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4f26ff',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4f26ff',
                },
              }}
            >
              {furnishingFacilitiesData.map((facility) => (
                <MenuItem
                  key={facility.name}
                  value={facility.name}
                  sx={{
                    fontFamily: 'Outfit',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box
                    component="img"
                    src={facility.icon}
                    alt={facility.name}
                    sx={{ width: 24, height: 24, objectFit: 'contain' }}
                  />
                  {facility.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Other Facilities Dropdown */}
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontFamily: 'Outfit', fontSize: '14px' }}>Other Facilities</InputLabel>
            <Select
              multiple
              value={selectedOther}
              onChange={handleOtherChange}
              input={<OutlinedInput label="Other Facilities" />}
              renderValue={(selected) => selected.length > 0 ? `${selected.length} selected` : ''}
              sx={{
                fontFamily: 'Outfit',
                fontSize: '14px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d0d0d0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4f26ff',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4f26ff',
                },
              }}
            >
              {otherFacilitiesData.map((facility) => (
                <MenuItem
                  key={facility}
                  value={facility}
                  sx={{
                    fontFamily: 'Outfit',
                    fontSize: '13px',
                  }}
                >
                  {facility}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Fixed height container for selected items */}
        <Box sx={{ minHeight: '180px', mb: 3 }}>
          {/* Display Selected Society Amenities */}
          {selectedSociety.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontFamily: 'Outfit',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#333',
                  mb: 1,
                }}
              >
                Society Amenities :
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedSociety.map((item) => {
                  const amenity = societyAmenitiesData.find(a => a.name === item);
                  return (
                    <Chip
                      key={item}
                      icon={
                        <Box
                          component="img"
                          src={amenity?.icon}
                          alt={item}
                          sx={{ width: 18, height: 18, objectFit: 'contain', ml: 0.5 }}
                        />
                      }
                      label={item}
                      onDelete={() => handleDeleteSociety(item)}
                      sx={{
                        fontFamily: 'Outfit',
                        fontSize: '12px',
                        backgroundColor: '#eeeaff',
                        color: '#4f26ff',
                        '& .MuiChip-deleteIcon': {
                          color: '#4f26ff',
                          '&:hover': {
                            color: '#3d1acc',
                          },
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Display Selected Furnishing Facilities */}
          {selectedFurnishing.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontFamily: 'Outfit',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#333',
                  mb: 1,
                }}
              >
                Furnishing Facilities :
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedFurnishing.map((item) => {
                  const facility = furnishingFacilitiesData.find(f => f.name === item);
                  return (
                    <Chip
                      key={item}
                      icon={
                        <Box
                          component="img"
                          src={facility?.icon}
                          alt={item}
                          sx={{ width: 18, height: 18, objectFit: 'contain', ml: 0.5 }}
                        />
                      }
                      label={item}
                      onDelete={() => handleDeleteFurnishing(item)}
                      sx={{
                        fontFamily: 'Outfit',
                        fontSize: '12px',
                        backgroundColor: '#eeeaff',
                        color: '#4f26ff',
                        '& .MuiChip-deleteIcon': {
                          color: '#4f26ff',
                          '&:hover': {
                            color: '#3d1acc',
                          },
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Display Selected Other Facilities */}
          {selectedOther.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontFamily: 'Outfit',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#333',
                  mb: 1,
                }}
              >
                Other Facilities :
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedOther.map((item) => (
                  <Chip
                    key={item}
                    label={item}
                    onDelete={() => handleDeleteOther(item)}
                    sx={{
                      fontFamily: 'Outfit',
                      fontSize: '12px',
                      backgroundColor: '#eeeaff',
                      color: '#4f26ff',
                      '& .MuiChip-deleteIcon': {
                        color: '#4f26ff',
                        '&:hover': {
                          color: '#3d1acc',
                        },
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mt: 'auto',
          }}
        >
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 1.5,
              textTransform: 'none',
              fontFamily: 'Outfit',
              fontSize: '13px',
              color: '#666',
              borderColor: '#e0e0e0',
              minWidth: { xs: '80px', sm: '100px' },
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 1.5,
              textTransform: 'none',
              fontFamily: 'Outfit',
              fontSize: '13px',
              backgroundColor: '#1976d2',
              minWidth: { xs: '80px', sm: '100px' },
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            Next
          </Button>
        </Box>
      </Paper>
    </PostAccess>
  );
};

export default Amenities;